import { renderAssetManifest, type RenderAssetStatus } from "./render-assets";

export type RenderGroupKind = "metal" | "gem" | "unassigned";
export type RenderEditorTab = "gem" | "metal" | "hdr" | "parts";

export type RenderMaterialDefinition = {
  id: string;
  label: string;
  prefix: string;
  color: string;
  thumbnailColor: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  envMapIntensity: number;
  reflectivity: number;
  thumbnailUrl?: string;
  baseColorMapUrl?: string;
  normalMapUrl?: string;
  aoMapUrl?: string;
  roughnessMapUrl?: string;
  metalnessMapUrl?: string;
  sourceLabel: string;
  status: RenderAssetStatus;
  notes: string;
};

export type RenderGemstoneDefinition = {
  id: string;
  label: string;
  prefix: string;
  color: string;
  thumbnailColor: string;
  transmission: number;
  roughness: number;
  ior: number;
  thickness: number;
  attenuationColor: string;
  attenuationDistance: number;
  dispersion: number;
  internalColorStrength: number;
  outerReflectionMix: number;
  maxOuterReflection: number;
  refractionDepth: number;
  refractionCount: number;
  maxReflection: number;
  mixedReflection: boolean;
  thumbnailUrl?: string;
  sourceLabel: string;
  status: RenderAssetStatus;
  notes: string;
};

export type RenderHdrDefinition = {
  id: string;
  label: string;
  alias: string;
  prefix: string;
  background: string;
  thumbnailColor: string;
  exposure: number;
  environmentIntensity: number;
  assetPath?: string;
  thumbnailUrl?: string;
  sourceLabel: string;
  status: RenderAssetStatus;
  notes: string;
};

export type RenderMeshGroup = {
  id: string;
  label: string;
  kind: RenderGroupKind;
  meshNames: string[];
};

export type RenderMeshAssignment = {
  meshName: string;
  groupId: string;
  kind: RenderGroupKind;
};

export type RenderGroupAppearance = {
  kind: RenderGroupKind;
  materialId?: string;
  gemstoneId?: string;
  baseColorMapUrl?: string;
  normalMapUrl?: string;
  aoMapUrl?: string;
  roughnessMapUrl?: string;
  metalnessMapUrl?: string;
  color: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  envMapIntensity: number;
  reflectivity: number;
  transmission: number;
  ior: number;
  thickness: number;
  attenuationColor: string;
  attenuationDistance: number;
  dispersion: number;
  internalColorStrength: number;
  outerReflectionMix: number;
  maxOuterReflection: number;
  refractionDepth: number;
  refractionCount: number;
  maxReflection: number;
  mixedReflection: boolean;
  hdr?: string;
  exposure?: number;
  backgroundColor: string;
  transparent: boolean;
  rotationEnabled: boolean;
};

export type RenderViewState = {
  showGrid: boolean;
  showAxes: boolean;
  preset?: string;
  cameraZoom?: number;
  cameraPosition?: [number, number, number];
  target?: [number, number, number];
  frustumSize?: number;
  cameraNear?: number;
  cameraFar?: number;
  controlsZoom0?: number;
  controlsMinZoom?: number;
  controlsMaxZoom?: number | null;
  controlsMinDistance?: number;
  controlsMaxDistance?: number;
  autoRotate?: boolean;
  autoRotateSpeed?: number;
};

export type RenderEditorConfig = {
  schemaVersion: 4;
  activeTab: RenderEditorTab;
  selectedGroupId?: string;
  hdr: string;
  exposure: number;
  backgroundColor: string;
  notes: string;
  showGrid: boolean;
  showAxes: boolean;
  view: RenderViewState;
  groups: Record<string, RenderGroupAppearance>;
  commerce?: {
    materialResourceId?: string;
    craftResourceId?: string;
  };
};

export type RenderSceneDocument = {
  schemaVersion: 4;
  source: "fbx-upload" | "stl-upload" | "legacy-stl-import" | "render-editor";
  model: {
    uploadedModelId?: string;
    storedPath?: string;
    checksum?: string;
    geometry?: {
      unit: "mm";
      width: number;
      height: number;
      depth: number;
      volume: number;
      surfaceArea: number;
      triangleCount: number;
    };
  };
  meshGroups: RenderMeshGroup[];
  meshAssignments: RenderMeshAssignment[];
};

export const DEFAULT_MATERIAL_ID = "metal-18k-white";
export const DEFAULT_GEMSTONE_ID = "gem-aquamarine";
export const DEFAULT_HDR_ID = "hdr-scene-32";

export const MATURE_RENDER_VIEW_PRESET: Omit<RenderViewState, "showGrid" | "showAxes"> = {
  preset: "starshine-mature-v1",
  cameraPosition: [0, 4, 7],
  target: [0, 0, 0],
  cameraZoom: 1.1,
  frustumSize: 6,
  cameraNear: 0.1,
  cameraFar: 1000,
  controlsZoom0: 1,
  controlsMinZoom: 0.35,
  controlsMaxZoom: null,
  controlsMinDistance: 0.1,
  controlsMaxDistance: 3.8,
  autoRotate: false,
  autoRotateSpeed: 2,
};

export const renderMaterialDefinitions: RenderMaterialDefinition[] = renderAssetManifest.metals.map((item) => ({
  id: item.id,
  label: item.label,
  prefix: item.prefix,
  color: item.color,
  thumbnailColor: item.swatch,
  metalness: item.metalness,
  roughness: item.roughness,
  clearcoat: item.clearcoat,
  envMapIntensity: item.envMapIntensity,
  reflectivity: item.reflectivity,
  thumbnailUrl: item.thumbnailUrl,
  baseColorMapUrl: item.baseColorMapUrl ?? undefined,
  normalMapUrl: item.normalMapUrl ?? undefined,
  aoMapUrl: item.aoMapUrl ?? undefined,
  roughnessMapUrl: item.roughnessMapUrl ?? undefined,
  metalnessMapUrl: item.metalnessMapUrl ?? undefined,
  sourceLabel: `${item.source.project}:${item.source.path}`,
  status: item.status,
  notes: item.notes,
}));

export const renderGemstoneDefinitions: RenderGemstoneDefinition[] = renderAssetManifest.gemstones.map((item) => ({
  id: item.id,
  label: item.label,
  prefix: item.prefix,
  color: item.color,
  thumbnailColor: item.swatch,
  transmission: item.transmission,
  roughness: item.roughness,
  ior: item.ior,
  thickness: item.thickness,
  attenuationColor: item.attenuationColor,
  attenuationDistance: item.attenuationDistance,
  dispersion: item.dispersion,
  internalColorStrength: item.internalColorStrength,
  outerReflectionMix: item.outerReflectionMix ?? (item.mixedReflection ? 0.55 : 0),
  maxOuterReflection: item.maxOuterReflection ?? item.maxReflection,
  refractionDepth: item.refractionDepth ?? item.thickness,
  refractionCount: item.refractionCount ?? 3,
  maxReflection: item.maxReflection,
  mixedReflection: item.mixedReflection,
  thumbnailUrl: item.thumbnailUrl,
  sourceLabel: `${item.source.project}:${item.source.path}`,
  status: item.status,
  notes: item.notes,
}));

export const renderHdrDefinitions: RenderHdrDefinition[] = renderAssetManifest.hdrs.map((item) => ({
  id: item.id,
  label: item.label,
  alias: item.alias,
  prefix: item.prefix,
  background: item.background,
  thumbnailColor: item.thumbnailColor,
  exposure: item.exposure,
  environmentIntensity: item.environmentIntensity,
  assetPath: item.hdrUrl,
  thumbnailUrl: item.thumbnailUrl,
  sourceLabel: `${item.source.project}:${item.source.path}`,
  status: item.status,
  notes: item.notes,
}));

export function getMaterialDefinition(id?: string) {
  return renderMaterialDefinitions.find((item) => item.id === id) ?? renderMaterialDefinitions[0];
}

export function getGemstoneDefinition(id?: string) {
  return renderGemstoneDefinitions.find((item) => item.id === id) ?? renderGemstoneDefinitions.find((item) => item.id === DEFAULT_GEMSTONE_ID) ?? renderGemstoneDefinitions[0];
}

export function getHdrDefinition(id?: string) {
  return renderHdrDefinitions.find((item) => item.id === id) ?? renderHdrDefinitions[0];
}

export function createDefaultAppearance(kind: RenderGroupKind, seed?: Partial<RenderGroupAppearance>): RenderGroupAppearance {
  const material = getMaterialDefinition(seed?.materialId);
  const gemstone = getGemstoneDefinition(seed?.gemstoneId);
  const isGem = kind === "gem";
  return {
    kind,
    materialId: isGem ? undefined : material.id,
    gemstoneId: isGem ? gemstone.id : undefined,
    baseColorMapUrl: seed?.baseColorMapUrl ?? material.baseColorMapUrl,
    normalMapUrl: seed?.normalMapUrl ?? material.normalMapUrl,
    aoMapUrl: seed?.aoMapUrl ?? material.aoMapUrl,
    roughnessMapUrl: seed?.roughnessMapUrl ?? material.roughnessMapUrl,
    metalnessMapUrl: seed?.metalnessMapUrl ?? material.metalnessMapUrl,
    color: seed?.color ?? (isGem ? gemstone.color : material.color),
    metalness: seed?.metalness ?? (isGem ? 0 : material.metalness),
    roughness: seed?.roughness ?? (isGem ? gemstone.roughness : material.roughness),
    clearcoat: seed?.clearcoat ?? (isGem ? 1 : material.clearcoat),
    envMapIntensity: seed?.envMapIntensity ?? (isGem ? 1.8 : material.envMapIntensity),
    reflectivity: seed?.reflectivity ?? material.reflectivity,
    transmission: seed?.transmission ?? (isGem ? gemstone.transmission : 0),
    ior: seed?.ior ?? (isGem ? gemstone.ior : 1.45),
    thickness: seed?.thickness ?? (isGem ? gemstone.thickness : 0),
    attenuationColor: seed?.attenuationColor ?? gemstone.attenuationColor,
    attenuationDistance: seed?.attenuationDistance ?? gemstone.attenuationDistance,
    dispersion: seed?.dispersion ?? gemstone.dispersion,
    internalColorStrength: seed?.internalColorStrength ?? gemstone.internalColorStrength,
    outerReflectionMix: seed?.outerReflectionMix ?? (seed?.mixedReflection === false ? 0 : gemstone.outerReflectionMix),
    maxOuterReflection: seed?.maxOuterReflection ?? seed?.maxReflection ?? gemstone.maxOuterReflection,
    refractionDepth: seed?.refractionDepth ?? gemstone.refractionDepth,
    refractionCount: seed?.refractionCount ?? gemstone.refractionCount,
    maxReflection: seed?.maxReflection ?? seed?.maxOuterReflection ?? gemstone.maxOuterReflection,
    mixedReflection: seed?.mixedReflection ?? ((seed?.outerReflectionMix ?? gemstone.outerReflectionMix) > 0),
    hdr: getHdrDefinition(seed?.hdr).id,
    exposure: typeof seed?.exposure === "number" ? seed.exposure : getHdrDefinition(seed?.hdr).exposure,
    backgroundColor: seed?.backgroundColor ?? "#000000",
    transparent: seed?.transparent ?? isGem,
    rotationEnabled: seed?.rotationEnabled ?? false,
  };
}

function parseJson<T>(input: unknown): Partial<T> | null {
  if (!input) return null;
  if (typeof input === "string") {
    try {
      return JSON.parse(input) as Partial<T>;
    } catch {
      return null;
    }
  }
  if (typeof input === "object") {
    return input as Partial<T>;
  }
  return null;
}

export function normalizeEditorConfig(input?: unknown): RenderEditorConfig {
  const parsed = parseJson<RenderEditorConfig & { metalColor?: string; gemColor?: string }>(input) ?? {};
  const hdr = getHdrDefinition(parsed.hdr).id;
  const parsedView: Partial<RenderViewState> = parsed.view && typeof parsed.view === "object" ? parsed.view : {};
  const rawGroups = parsed.groups && typeof parsed.groups === "object" ? parsed.groups : {};
  const groups = Object.entries(rawGroups).reduce<Record<string, RenderGroupAppearance>>((next, [id, value]) => {
    const seed = value && typeof value === "object" ? (value as Partial<RenderGroupAppearance>) : {};
    next[id] = createDefaultAppearance(seed.kind ?? "metal", seed);
    return next;
  }, {});

  if (!groups.metal) {
    groups.metal = createDefaultAppearance("metal", { color: parsed.metalColor });
  }
  if (!groups.gemstone) {
    groups.gemstone = createDefaultAppearance("gem", { color: parsed.gemColor });
  }
  if (!groups.unassigned) {
    groups.unassigned = createDefaultAppearance("unassigned", { color: "#d8d8d8" });
  }

  return {
    schemaVersion: 4,
    activeTab: parsed.activeTab ?? "gem",
    selectedGroupId: parsed.selectedGroupId ?? "gemstone",
    hdr,
    exposure: typeof parsed.exposure === "number" ? parsed.exposure : getHdrDefinition(hdr).exposure,
    backgroundColor: typeof parsed.backgroundColor === "string" ? parsed.backgroundColor : "#000000",
    notes: typeof parsed.notes === "string" ? parsed.notes : "",
    showGrid: parsed.showGrid ?? parsed.view?.showGrid ?? false,
    showAxes: parsed.showAxes ?? parsed.view?.showAxes ?? false,
    view: {
      ...MATURE_RENDER_VIEW_PRESET,
      showGrid: parsed.showGrid ?? parsed.view?.showGrid ?? false,
      showAxes: parsed.showAxes ?? parsed.view?.showAxes ?? false,
      preset: parsedView.preset ?? MATURE_RENDER_VIEW_PRESET.preset,
      cameraZoom: typeof parsedView.cameraZoom === "number" ? parsedView.cameraZoom : MATURE_RENDER_VIEW_PRESET.cameraZoom,
      cameraPosition: Array.isArray(parsedView.cameraPosition) ? parsedView.cameraPosition : MATURE_RENDER_VIEW_PRESET.cameraPosition,
      target: Array.isArray(parsedView.target) ? parsedView.target : MATURE_RENDER_VIEW_PRESET.target,
      frustumSize: typeof parsedView.frustumSize === "number" ? parsedView.frustumSize : MATURE_RENDER_VIEW_PRESET.frustumSize,
      cameraNear: typeof parsedView.cameraNear === "number" ? parsedView.cameraNear : MATURE_RENDER_VIEW_PRESET.cameraNear,
      cameraFar: typeof parsedView.cameraFar === "number" ? parsedView.cameraFar : MATURE_RENDER_VIEW_PRESET.cameraFar,
      controlsZoom0: typeof parsedView.controlsZoom0 === "number" ? parsedView.controlsZoom0 : MATURE_RENDER_VIEW_PRESET.controlsZoom0,
      controlsMinZoom: typeof parsedView.controlsMinZoom === "number" ? parsedView.controlsMinZoom : MATURE_RENDER_VIEW_PRESET.controlsMinZoom,
      controlsMaxZoom: typeof parsedView.controlsMaxZoom === "number" || parsedView.controlsMaxZoom === null ? parsedView.controlsMaxZoom : MATURE_RENDER_VIEW_PRESET.controlsMaxZoom,
      controlsMinDistance: typeof parsedView.controlsMinDistance === "number" ? parsedView.controlsMinDistance : MATURE_RENDER_VIEW_PRESET.controlsMinDistance,
      controlsMaxDistance: typeof parsedView.controlsMaxDistance === "number" ? parsedView.controlsMaxDistance : MATURE_RENDER_VIEW_PRESET.controlsMaxDistance,
      autoRotate: typeof parsedView.autoRotate === "boolean" ? parsedView.autoRotate : MATURE_RENDER_VIEW_PRESET.autoRotate,
      autoRotateSpeed: typeof parsedView.autoRotateSpeed === "number" ? parsedView.autoRotateSpeed : MATURE_RENDER_VIEW_PRESET.autoRotateSpeed,
    },
    groups,
    commerce: parsed.commerce && typeof parsed.commerce === "object" ? {
      materialResourceId: parsed.commerce.materialResourceId,
      craftResourceId: parsed.commerce.craftResourceId,
    } : undefined,
  };
}

export function normalizeSceneDocument(input?: unknown, uploadedModelId?: string): RenderSceneDocument {
  const parsed = parseJson<RenderSceneDocument>(input) ?? {};
  const meshGroups = Array.isArray(parsed.meshGroups) ? parsed.meshGroups : [];
  const meshAssignments = Array.isArray(parsed.meshAssignments) ? parsed.meshAssignments : [];

  return {
    schemaVersion: 4,
    source: parsed.source ?? "fbx-upload",
    model: {
      uploadedModelId: parsed.model?.uploadedModelId ?? uploadedModelId,
      storedPath: parsed.model?.storedPath,
      checksum: parsed.model?.checksum,
      geometry: parsed.model?.geometry,
    },
    meshGroups,
    meshAssignments,
  };
}

export function serializeRenderState(scene: RenderSceneDocument, config: RenderEditorConfig) {
  return {
    sceneJson: JSON.stringify({ ...scene, schemaVersion: 4 }),
    configJson: JSON.stringify({ ...config, schemaVersion: 4 }),
  };
}
